import { useEffect, useState } from 'react';

const isRenderCancelled = (error) =>
    error?.name === 'RenderingCancelledException' || error?.name === 'AbortException';

export default function usePdfPageRender({ pdf, pageNumber, scale, canvasRef }) {
    const [isRendering, setIsRendering] = useState(false);
    const [renderError, setRenderError] = useState(null);
    const [renderGeneration, setRenderGeneration] = useState(0);

    useEffect(() => {
        if (!pdf || !pageNumber || !canvasRef?.current) {
            return undefined;
        }

        let cancelled = false;
        let renderTask = null;

        const renderPage = async () => {
            setIsRendering(true);
            setRenderError(null);

            try {
                const page = await pdf.getPage(pageNumber);

                if (cancelled || !canvasRef.current) {
                    return;
                }

                const viewport = page.getViewport({ scale });
                const canvas = canvasRef.current;
                const context = canvas.getContext('2d');
                const outputScale = window.devicePixelRatio || 1;

                canvas.width = Math.floor(viewport.width * outputScale);
                canvas.height = Math.floor(viewport.height * outputScale);
                canvas.style.width = `${Math.floor(viewport.width)}px`;
                canvas.style.height = `${Math.floor(viewport.height)}px`;

                const transform = outputScale !== 1 ? [outputScale, 0, 0, outputScale, 0, 0] : null;

                renderTask = page.render({
                    canvasContext: context,
                    viewport,
                    transform,
                });

                await renderTask.promise;

                if (!cancelled) {
                    setIsRendering(false);
                    setRenderGeneration((value) => value + 1);
                }
            } catch (pageError) {
                if (cancelled || isRenderCancelled(pageError)) {
                    return;
                }

                console.error(pageError);
                setRenderError(pageError);
                setIsRendering(false);
            }
        };

        renderPage();

        return () => {
            cancelled = true;
            renderTask?.cancel();
        };
    }, [pdf, pageNumber, scale, canvasRef]);

    return { isRendering, renderError, renderGeneration };
}
